import { useMemo, useState } from "react";
import { DEMO_ORG } from "../../lib/roles";
import {
  formatPeriodLabel,
  isEditableStatus,
  type TimesheetHeader,
} from "../../lib/timekeeping";

const AS_OF = "2026-07-27";

type Bucket = "onTime" | "late" | "submitted";

function classify(s: TimesheetHeader): Bucket {
  if (s.status === "Submitted" || s.status === "Correction requested") return "submitted";
  if (isEditableStatus(s.status) && s.due_at < AS_OF) return "late";
  if (s.status === "Approved") return "onTime";
  if (isEditableStatus(s.status) && s.due_at >= AS_OF) return "onTime";
  return "late";
}

export function ComplianceOpsPanel({
  sheets,
  roleIds,
}: {
  sheets: TimesheetHeader[];
  roleIds: string[];
}) {
  const isPayroll = roleIds.includes("ROLE-PAYROLL") || roleIds.includes("ROLE-HR-PAYROLL");
  const isSupervisor = roleIds.includes("ROLE-SUPERVISOR");
  const isPm = roleIds.includes("ROLE-PROJECT-MANAGER");
  const isCompanyWide =
    isPayroll ||
    roleIds.includes("ROLE-FINANCE-ACCOUNTING") ||
    roleIds.includes("ROLE-EXECUTIVE-BI-AUDITOR");

  const [view, setView] = useState<"supervisor" | "person">(
    isPm ? "supervisor" : "person",
  );
  const [badOnly, setBadOnly] = useState(true);

  const scoped = useMemo(() => {
    if (isCompanyWide) return sheets;
    if (isSupervisor) {
      const reports = DEMO_ORG.supervisors["emp-ortiz"]?.reportIds ?? [];
      // Demo signed-in supervisor is Ortiz when previewing supervisor role
      return sheets.filter((s) => reports.includes(s.employee_id) || s.employee_id === "emp-ortiz");
    }
    if (isPm) {
      const reportIds = new Set<string>();
      for (const sid of DEMO_ORG.pmOverseesSupervisors) {
        reportIds.add(sid);
        for (const id of DEMO_ORG.supervisors[sid]?.reportIds ?? []) reportIds.add(id);
      }
      return sheets.filter((s) => reportIds.has(s.employee_id));
    }
    return sheets;
  }, [sheets, isCompanyWide, isSupervisor, isPm]);

  const periods = useMemo(() => {
    const map = new Map<string, { start: string; end: string; onTime: number; late: number; submitted: number }>();
    for (const s of scoped) {
      const key = s.period_start;
      const row = map.get(key) ?? {
        start: s.period_start,
        end: s.period_end,
        onTime: 0,
        late: 0,
        submitted: 0,
      };
      const b = classify(s);
      if (b === "onTime") row.onTime += 1;
      else if (b === "late") row.late += 1;
      else row.submitted += 1;
      map.set(key, row);
    }
    return [...map.values()].sort((a, b) => a.start.localeCompare(b.start));
  }, [scoped]);

  const byPerson = useMemo(() => {
    type PersonRow = {
      id: string;
      name: string;
      late: number;
      total: number;
      marks: { period: string; late: boolean }[];
    };
    const map = new Map<string, PersonRow>();
    for (const s of scoped) {
      const row = map.get(s.employee_id) ?? {
        id: s.employee_id,
        name: s.employee,
        late: 0,
        total: 0,
        marks: [] as { period: string; late: boolean }[],
      };
      const late = classify(s) === "late";
      row.total += 1;
      if (late) row.late += 1;
      row.marks.push({ period: s.period_start, late });
      map.set(s.employee_id, row);
    }
    let rows = [...map.values()];
    if (badOnly) rows = rows.filter((r) => r.late >= 2);
    return rows.sort((a, b) => b.late - a.late || a.name.localeCompare(b.name));
  }, [scoped, badOnly]);

  const bySupervisor = useMemo(() => {
    const rows: {
      id: string;
      name: string;
      late: number;
      total: number;
    }[] = [];
    for (const [sid, info] of Object.entries(DEMO_ORG.supervisors) as [
      string,
      { name: string; reportIds: string[] },
    ][]) {
      const ids = new Set([sid, ...info.reportIds]);
      const subset = scoped.filter((s) => ids.has(s.employee_id));
      const late = subset.filter((s) => classify(s) === "late").length;
      rows.push({ id: sid, name: info.name, late, total: subset.length });
    }
    return badOnly ? rows.filter((r) => r.late >= 2) : rows;
  }, [scoped, badOnly]);

  const maxBar = Math.max(
    ...periods.map((p) => p.onTime + p.late + p.submitted),
    1,
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm text-slate">
          Scope:{" "}
          {isCompanyWide
            ? "Company-wide (Payroll)"
            : isPm
              ? "PM echelon"
              : isSupervisor
                ? "Direct reports"
                : "Assigned"}
        </span>
        {isPm && (
          <div className="flex gap-1 ml-2">
            <button
              type="button"
              className={`px-2 py-1 text-xs ${view === "supervisor" ? "bg-teal text-white" : "border border-black/10"}`}
              onClick={() => setView("supervisor")}
            >
              By supervisor
            </button>
            <button
              type="button"
              className={`px-2 py-1 text-xs ${view === "person" ? "bg-teal text-white" : "border border-black/10"}`}
              onClick={() => setView("person")}
            >
              By person
            </button>
          </div>
        )}
        <label className="ml-auto text-sm text-navy flex items-center gap-2">
          <input
            type="checkbox"
            checked={badOnly}
            onChange={(e) => setBadOnly(e.target.checked)}
          />
          Bad actors only (≥2 late periods)
        </label>
      </div>

      <div className="bg-white border border-black/5 p-4">
        <h2 className="font-semibold text-navy mb-1">Period close trends</h2>
        <p className="text-xs text-slate mb-3">
          Per period: on-time (teal), late/missing (red), submitted awaiting approval (gold).
        </p>
        <div className="flex items-end gap-3 h-40">
          {periods.map((p) => {
            const total = p.onTime + p.late + p.submitted || 1;
            return (
              <div key={p.start} className="flex-1 flex flex-col items-center gap-1 min-w-[56px]">
                <div
                  className="w-full flex flex-col justify-end"
                  style={{ height: `${(total / maxBar) * 100}%`, minHeight: 8 }}
                  title={`On-time ${p.onTime} · Late ${p.late} · Submitted ${p.submitted}`}
                >
                  <div
                    className="w-full bg-red-500/80"
                    style={{ height: `${(p.late / total) * 100}%` }}
                  />
                  <div
                    className="w-full bg-amber-400/90"
                    style={{ height: `${(p.submitted / total) * 100}%` }}
                  />
                  <div
                    className="w-full bg-teal/80"
                    style={{ height: `${(p.onTime / total) * 100}%` }}
                  />
                </div>
                <span className="text-[10px] text-slate text-center leading-tight">
                  {formatPeriodLabel(p.start, p.end).split("–")[0]}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-black/5 overflow-hidden">
        <div className="px-4 py-3 border-b border-black/5 font-semibold text-navy">
          {isPm && view === "supervisor" ? "Supervisors" : "People"} — lateness report card
        </div>
        <table className="w-full text-sm">
          <thead className="bg-mist">
            <tr>
              <th className="text-left px-3 py-2 text-slate">Name</th>
              <th className="text-left px-3 py-2 text-slate">Late periods</th>
              <th className="text-left px-3 py-2 text-slate">Trend</th>
            </tr>
          </thead>
          <tbody>
            {(isPm && view === "supervisor" ? bySupervisor : byPerson).map((r) => (
              <tr key={r.id} className="border-t border-black/5">
                <td className="px-3 py-2 font-medium text-navy">{r.name}</td>
                <td className="px-3 py-2 text-red-700">
                  {r.late} / {r.total}
                </td>
                <td className="px-3 py-2">
                  {"marks" in r ? (
                    <div className="flex gap-0.5">
                      {(r as { marks: { period: string; late: boolean }[] }).marks
                        .sort((a, b) => a.period.localeCompare(b.period))
                        .map((m) => (
                          <span
                            key={m.period}
                            className={`w-2.5 h-4 ${m.late ? "bg-red-500" : "bg-teal/70"}`}
                            title={`${m.period}: ${m.late ? "late" : "ok"}`}
                          />
                        ))}
                    </div>
                  ) : (
                    <span className="text-xs text-slate">See by-person for marks</span>
                  )}
                </td>
              </tr>
            ))}
            {(isPm && view === "supervisor" ? bySupervisor : byPerson).length === 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-4 text-sm text-slate">
                  No chronic late submitters in this scope
                  {badOnly ? " (clear the filter to see everyone)" : ""}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-black/5 overflow-hidden">
        <div className="px-4 py-3 border-b border-black/5 font-semibold text-navy">
          Current queue
        </div>
        <table className="w-full text-sm">
          <thead className="bg-mist">
            <tr>
              <th className="text-left px-3 py-2 text-slate">Employee</th>
              <th className="text-left px-3 py-2 text-slate">Period</th>
              <th className="text-left px-3 py-2 text-slate">Due</th>
              <th className="text-left px-3 py-2 text-slate">Flag</th>
            </tr>
          </thead>
          <tbody>
            {[...scoped]
              .sort((a, b) => b.period_start.localeCompare(a.period_start))
              .slice(0, 24)
              .map((s) => {
                const b = classify(s);
                const flag =
                  b === "late"
                    ? "Missing / late"
                    : b === "submitted"
                      ? "Awaiting approval"
                      : "On time";
                return (
                  <tr key={s.id} className="border-t border-black/5">
                    <td className="px-3 py-2">{s.employee}</td>
                    <td className="px-3 py-2">
                      {formatPeriodLabel(s.period_start, s.period_end)}
                    </td>
                    <td className="px-3 py-2">{s.due_at.slice(0, 10)}</td>
                    <td
                      className={`px-3 py-2 ${
                        b === "late" ? "text-red-700 font-medium" : "text-slate"
                      }`}
                    >
                      {flag}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
